<template>
  <div>
    <v-toolbar color="secondary" flat>
      <v-btn icon @click="goBack">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-toolbar-title>{{ $t('Auswertung Detailansicht (Nur Lesezugriff)') }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        class="mr-4"
        :loading="isDownloadingPdf"
        :disabled="isDownloadingPdf"
        @click="downloadResultsPdf"
      >
        <v-icon left>mdi-file-pdf-box</v-icon>
        {{ $t('Ergebnisse herunterladen') }}
      </v-btn>
      <v-chip color="warning" class="mr-4">{{ $t('Schreibgeschützt') }}</v-chip>
    </v-toolbar>
    <!-- Wir betten hier die InspectionPointView ein, müssen aber sicherstellen, dass sie im Read-Only Modus ist -->
    <InspectionPointView :readOnly="true" />
  </div>
</template>

<script>
import { ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import InspectionPointView from '../InspectionPointView.vue';
import APIService from '@/services/APIService';
import APIServiceRestDB from '@/services/MesskursBackendService';
import { convertJsonArraysToJs, floatsToLocal } from '@/tools/utils';
import i18n from '@/i18n';
import de from '@/locales/de.json';
import en from '@/locales/en.json';
import {
  buildEvaluationPdfRows,
  downloadEvaluationPdf,
  EVALUATION_PDF_FIELDS
} from '@/services/evaluationPdfGenerator';

export default {
  name: 'EvaluationDetailView',
  components: {
    InspectionPointView
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const messages = { de, en };
    const t = (key) => {
      const currentLocale = i18n.global?.locale?.value || i18n.global?.locale || 'de';
      return messages[currentLocale]?.[key] || messages.en[key] || key;
    };
    const userId = route.params.userId;
    const isDownloadingPdf = ref(false);

    const goBack = () => {
      router.push({ name: 'EvaluationUserTests', params: { userId: userId } });
    };

    const parseSessionResults = (resultsValue) => {
      if (!resultsValue) return {};
      if (typeof resultsValue === 'object') return resultsValue;
      try {
        return JSON.parse(resultsValue);
      } catch (error) {
        console.error("Error parsing session results for PDF:", error);
        return {};
      }
    };

    const cloneWithLocalFloats = (value) => {
      const clone = JSON.parse(JSON.stringify(value || {}));
      floatsToLocal(clone);
      return clone;
    };

    const getSessionItems = (session) => [
      ...(Array.isArray(session?.items) ? session.items : []),
      ...(Array.isArray(session?.itemsObj) ? session.itemsObj : [])
    ];

    const sameId = (left, right) => !!left && !!right && String(left) === String(right);

    const getDisplayName = (user) => {
      if (!user) return '';
      if (typeof user === 'string') return user;
      return user.name || user.username || user.email || user.id || user._id || '';
    };

    const getSessionUserDisplayName = (session) => {
      return session.userName
        || session.username
        || session.userEmail
        || getDisplayName(session.user)
        || '';
    };

    const loadUserDisplayName = async (session) => {
      const sessionUserName = getSessionUserDisplayName(session);
      if (sessionUserName && sessionUserName !== userId) return sessionUserName;

      const user = await APIService.getUser(userId).catch(() => null);
      return getDisplayName(user) || sessionUserName || userId;
    };

    const sessionMatchesWorkpieceItem = (item, workpiece) => {
      if (!item || !workpiece?._id) return false;
      if (typeof item !== 'object') return sameId(item, workpiece._id);

      return sameId(item._id, workpiece._id)
        || sameId(item.workpiece, workpiece._id)
        || sameId(item.workpieceId, workpiece._id)
        || sameId(item.toolbox, workpiece._id);
    };

    const sessionMatchesInspectionPointItem = (item, inspectionPoint) => {
      if (!item || !inspectionPoint?._id) return false;
      if (typeof item !== 'object') return sameId(item, inspectionPoint._id);

      return sameId(item._id, inspectionPoint._id)
        || sameId(item.checkpoint, inspectionPoint._id)
        || sameId(item.checkpointId, inspectionPoint._id);
    };

    const filterWorkpiecesForSession = (workpieces, session) => {
      const sessionItems = getSessionItems(session);
      if (sessionItems.length === 0) return workpieces;

      const filtered = workpieces.filter(wp =>
        sessionItems.some(item => sessionMatchesWorkpieceItem(item, wp))
      );
      return filtered.length > 0 ? filtered : [];
    };

    const filterCheckpointsForSession = (checkpoints, session, workpiece) => {
      const sessionItems = getSessionItems(session);
      if (sessionItems.length === 0) return checkpoints;

      const filtered = checkpoints.filter(point =>
        sessionItems.some(item => sessionMatchesInspectionPointItem(item, point))
      );
      if (filtered.length > 0) return filtered;
      return sessionItems.some(item => sessionMatchesWorkpieceItem(item, workpiece)) ? checkpoints : [];
    };

    const loadEvaluationPdfData = async (session) => {
      const caseId = route.query.case || session.object || session.caseId || session.objectSpecId || session.toolbox || null;
      if (!caseId) throw new Error("No case ID found for PDF export");

      let caseData = store.state.actualCase;
      if (!caseData || caseData._id !== caseId) {
        caseData = await APIServiceRestDB.getObjectWithId(caseId);
      }
      if (!caseData?.objectSpecId) throw new Error("No objectSpecId found for PDF export");

      const specs = convertJsonArraysToJs(await APIServiceRestDB.getToolbox(caseData.objectSpecId));
      const allWorkpieces = convertJsonArraysToJs(await APIServiceRestDB.getWorkpiecesForCase(specs._id));
      const workpieces = filterWorkpiecesForSession(allWorkpieces, session);
      const resultsByPointId = parseSessionResults(session.results);
      const masterDefinitionsByPointId = {};

      const workpiecesWithInspectionPoints = await Promise.all(workpieces.map(async workpiece => {
        const checkpoints = filterCheckpointsForSession(
          await APIServiceRestDB.getCheckpoints(workpiece._id),
          session,
          workpiece
        );

        await Promise.all(checkpoints.map(async point => {
          if (masterDefinitionsByPointId[point._id]) return;
          masterDefinitionsByPointId[point._id] = cloneWithLocalFloats(
            await APIServiceRestDB.getCheckpointSettings(caseData._id, point._id)
          );
          if (resultsByPointId[point._id]) {
            resultsByPointId[point._id] = cloneWithLocalFloats(resultsByPointId[point._id]);
          }
        }));

        return {
          ...workpiece,
          inspectionPoints: checkpoints
        };
      }));

      return {
        caseData,
        workpieces: workpiecesWithInspectionPoints,
        resultsByPointId,
        masterDefinitionsByPointId
      };
    };

    const downloadResultsPdf = async () => {
      if (isDownloadingPdf.value) {
        return;
      }
      isDownloadingPdf.value = true;

      try {
        const sessionId = store.state.sessionId || route.params.sessionId || route.query.session;
        const freshSession = sessionId ? await APIServiceRestDB.getSession(sessionId) : null;
        const session = freshSession || store.state.session;
        if (!session) throw new Error("No session found for PDF export");
        store.commit('setSession', session);

        const reportData = await loadEvaluationPdfData(session);
        const userDisplayName = await loadUserDisplayName(session);
        const fieldLabels = EVALUATION_PDF_FIELDS.reduce((labels, field) => {
          labels[field.key] = t(field.labelKey);
          return labels;
        }, {});
        const statusLabels = {
          correct: t('Richtig'),
          wrong: t('Falsch'),
          open: t('Offen')
        };

        const rows = buildEvaluationPdfRows({
          workpieces: reportData.workpieces,
          resultsByPointId: reportData.resultsByPointId,
          masterDefinitionsByPointId: reportData.masterDefinitionsByPointId,
          fieldLabels,
          statusLabels
        });

        downloadEvaluationPdf({
          title: t('Auswertung'),
          subtitle: `${session.title || reportData.caseData.title || t('Unbekannte Prüfung')} - ${userDisplayName}`,
          rows,
          filename: `messkurs-auswertung-${session._id || sessionId || Date.now()}`,
          tableLabels: {
            workpiece: t('Name of workpiece'),
            inspectionPoint: t('Inspection Point'),
            pointStatus: t('Prüfstelle'),
            field: t('Feld'),
            fields: t('Felder'),
            inspectionPoints: t('Inspection Points'),
            traineeValue: t('Azubi-Wert'),
            masterValue: t('Sollwert'),
            status: t('Bewertung'),
            page: t('Seite')
          },
          statusLabels
        });
      } catch (error) {
        console.error("Error creating evaluation PDF:", error);
        alert(t('Die PDF konnte nicht erstellt werden.'));
      } finally {
        isDownloadingPdf.value = false;
      }
    };

    return {
      goBack,
      isDownloadingPdf,
      downloadResultsPdf
    };
  }
};
</script>
